import { h, Fragment } from 'preact';
import { useRef, useState } from 'preact/hooks';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Scrollbar } from 'swiper';
import './product-recommendations.css';
import { IconRightArrow, IconArrowCurvy } from 'utils/icon';
import Image from 'utils/image';
import ProductCard from 'utils/product-card';
import useWindowSize from 'utils/useWindowSize';
import WishlistContext from '../utils/gift-reggie/wishlist-context';

const ProductRecommendationsTabs = (props) => {
  const {
    sectionTitle,
    tabs,
    textBlock,
    contentBlock,
    sectionId,
    ctaLink,
    ctaText,
    layout,
    lazyload,
  } = props;

  const [activeTab, setActiveTab] = useState(0);
  const [imageHeight, setImageHeight] = useState(400);
  const [isDesktop, setIsDesktop] = useState(layout.isAboveBreakpoint('lg'));
  const handleBpChange = () => {
    setIsDesktop(layout.isAboveBreakpoint('lg'));
  };
  window.addEventListener('bpChange', handleBpChange);
  const hasTextBlock =
    textBlock.heading ||
    textBlock.eyebrow ||
    textBlock.body ||
    (textBlock.link && textBlock.linkText);
  const hasContentBlock =
    contentBlock.heading ||
    contentBlock.body ||
    (contentBlock.link && contentBlock.linkText);

  return (
    <WishlistContext>
      <div className="text-center">
        {sectionTitle && (
          <h2 className={`h2 mb-5 ${tabs.length ? 'lg:mb-9' : ''}`}>
            {sectionTitle}
          </h2>
        )}
        {tabs.length ? (
          <div>
            <ul className="flex w-full mb-8 lg:mb-10 lg:justify-center gap-7 overflow-x-auto no-scrollbar">
              {tabs.map((tab, index) => (
                <li
                  key={index}
                  className={`cursor-pointer h7--medium whitespace-nowrap ${
                    index === activeTab
                      ? 'text-text-default underline underline-offset-4'
                      : 'text-text-disabled'
                  }`}
                  onClick={() => setActiveTab(index)}
                >
                  {tab.title}
                </li>
              ))}
            </ul>
            <div>
              <ProductRecommendations
                tabsLength={tabs.length}
                products={tabs[activeTab].products}
                sectionId={sectionId}
                imageHeight={imageHeight}
                setImageHeight={setImageHeight}
                lazyload={lazyload}
                hasContentBlock={
                  tabs[activeTab].contentBlock.heading ||
                  tabs[activeTab].contentBlock.body ||
                  (tabs[activeTab].contentBlock.link &&
                    tabs[activeTab].contentBlock.linkText)
                }
                contentBlock={tabs[activeTab].contentBlock}
              />
            </div>
          </div>
        ) : (
          <div className="lg:flex">
            {hasTextBlock && Number(textBlock.textBlockPosition) === 0 && (
              <TextBlock
                content={textBlock}
                imageHeight={imageHeight}
                isDesktop={isDesktop}
              />
            )}
            <ProductRecommendations
              products={props.products}
              hasTextBlock={hasTextBlock}
              hasContentBlock={hasContentBlock}
              contentBlock={contentBlock}
              textBlock={textBlock}
              sectionId={sectionId}
              lazyload={lazyload}
              imageHeight={imageHeight}
              setImageHeight={setImageHeight}
              isDesktop={isDesktop}
            />
            {textBlock.link && textBlock.linkText && (
              <a
                href={textBlock.link}
                className="lg:hidden button button--primary no-underline text-center w-[calc(100%-40px)] mx-auto mt-5 block "
              >
                {textBlock.linkText}
              </a>
            )}
          </div>
        )}
        {ctaLink && ctaText && (
          <div className="px-5 mt-5 lg:mt-14">
            <a
              href={ctaLink}
              className="button button--primary no-underline text-center w-full lg:w-[180px]"
            >
              {ctaText}
            </a>
          </div>
        )}
      </div>
    </WishlistContext>
  );
};

const ProductRecommendations = ({
  products,
  tabsLength,
  hasContentBlock,
  contentBlock,
  hasTextBlock,
  textBlock,
  sectionId,
  imageHeight,
  setImageHeight,
  lazyload,
  isDesktop,
}) => {
  const swiper = useRef();
  const contentBlockPosition = Number(contentBlock?.contentBlockPosition);
  const textBlockPosition = Number(textBlock?.textBlockPosition);
  const { isMobile } = useWindowSize();

  const nextArrowClass = `.swiper-button-next-${sectionId}`;
  const prevArrowClass = `.swiper-button-prev-${sectionId}`;

  return (
    products.length > 0 && (
      <div
        className={`text-center lg:!px-10 overflow-hidden ${
          tabsLength ? '' : '!px-5'
        }`}
        data-product-recommendations
        ref={swiper}
      >
        <Swiper
          modules={[Navigation, Scrollbar]}
          observer={true}
          threshold={10}
          onObserverUpdate={() => {
            if (swiper.current) {
              const productImageSelector = isMobile
                ? '.product-card .swiper-slide img'
                : '.product-card img';
              const productImageHeight =
                swiper.current.querySelector(
                  productImageSelector,
                )?.offsetHeight;
              if (productImageHeight) setImageHeight(productImageHeight);
            }
          }}
          navigation={{
            nextEl: nextArrowClass,
            prevEl: prevArrowClass,
            disabledClass: '!opacity-0',
          }}
          slidesPerView={tabsLength ? 2 : 1.8}
          spaceBetween={6}
          scrollbar={{
            el: '.swiper-scrollbar',
            draggable: true,
          }}
          breakpoints={{
            1024: {
              slidesPerView: products.length < 4 ? products.length : 4,
            },
          }}
          className="relative w-full product-recommendations"
        >
          <div
            className={`${`swiper-button-prev-${sectionId}`} swiper-button-prev
            cursor-pointer absolute hidden lg:flex h-[42px] w-[42px]
            rotate-180 items-center justify-center
            bg-white drop-shadow-md rounded-full origin-center
            transition-opacity -translate-y-1/2 left-14 z-1`}
            style={{ top: imageHeight / 2 }}
          >
            <IconRightArrow />
          </div>
          <div
            className={`${`swiper-button-next-${sectionId}`} swiper-button-next
            cursor-pointer absolute hidden lg:flex h-[42px] w-[42px]
            items-center justify-center rounded-full
            bg-white drop-shadow-md transition-opacity
            -translate-y-1/2 right-14 z-1`}
            style={{ top: imageHeight / 2 }}
          >
            <IconRightArrow />
          </div>
          {products.map((product, index) => {
            const isLazyLoad = lazyload || index > 3;
            const position = index + 1;

            const adjustedContentBlockPosition =
              hasTextBlock &&
              textBlockPosition > 0 &&
              textBlockPosition < contentBlockPosition
                ? contentBlockPosition - 1
                : contentBlockPosition;

            const adjustedTextBlockPosition =
              hasContentBlock && contentBlockPosition < textBlockPosition
                ? textBlockPosition - 1
                : textBlockPosition;

            const shouldInsertContentBlock =
              hasContentBlock && adjustedContentBlockPosition === position;
            const shouldInsertTextBlock =
              hasTextBlock &&
              textBlockPosition > 0 &&
              adjustedTextBlockPosition === position;

            return (
              <Fragment key={`${product.id}-${index}`}>
                {contentBlockPosition < textBlockPosition &&
                  shouldInsertContentBlock && (
                    <SwiperSlide className="md:w-[335px] w-[184px]">
                      <ContentBlock
                        lazyload={lazyload}
                        content={contentBlock}
                        imageHeight={imageHeight}
                      />
                    </SwiperSlide>
                  )}
                {shouldInsertTextBlock && (
                  <SwiperSlide className="md:w-[335px] w-[184px]">
                    <TextBlock
                      content={textBlock}
                      imageHeight={imageHeight}
                      isDesktop={isDesktop}
                    />
                  </SwiperSlide>
                )}
                {contentBlockPosition >= textBlockPosition &&
                  shouldInsertContentBlock && (
                    <SwiperSlide className="md:w-[335px] w-[184px]">
                      <ContentBlock
                        lazyload={lazyload}
                        content={contentBlock}
                        imageHeight={imageHeight}
                      />
                    </SwiperSlide>
                  )}
                <SwiperSlide className="md:w-[335px] w-[184px]">
                  <ProductCard
                    product={product}
                    swipeSlider={false}
                    addToCartAttributeName={'product_recommendations'}
                    lazyload={isLazyLoad}
                  />
                </SwiperSlide>
              </Fragment>
            );
          })}
          {hasTextBlock && textBlockPosition > products.length && (
            <SwiperSlide className="md:w-[335px] w-[184px]">
              <TextBlock
                content={textBlock}
                imageHeight={imageHeight}
                isDesktop={isDesktop}
              />
            </SwiperSlide>
          )}
          {hasContentBlock && contentBlockPosition > products.length && (
            <SwiperSlide className="md:w-[335px] w-[184px]">
              <ContentBlock
                lazyload={lazyload}
                content={contentBlock}
                imageHeight={imageHeight}
              />
            </SwiperSlide>
          )}
          <div
            className={`swiper-scrollbar lg:!hidden !h-0.5
            !relative mt-7 lg:mt-10 !inset-auto ${
              tabsLength ? '!w-[calc(100%-40px)] !mx-auto' : ''
            }`}
          ></div>
        </Swiper>
      </div>
    )
  );
};

const TextBlock = ({ content, imageHeight, isDesktop }) => (
  <div
    className={`text-left z-[2] flex items-center px-5 ${
      Number(content.textBlockPosition) === 0
        ? 'lg:w-1/5 lg:min-w-1/5 lg:pl-10 lg:pr-0 relative'
        : ''
    }`}
    {...(Number(content.textBlockPosition) !== 0 ||
    (Number(content.textBlockPosition) === 0 && isDesktop)
      ? { style: { height: imageHeight } }
      : {})}
  >
    <div>
      {content.eyebrow && (
        <h6 className="h7--medium mb-1.5 lg:mb-2 text-text-subdued">
          {content.eyebrow}
        </h6>
      )}
      {content.heading && (
        <h3 className="h2 mb-2 lg:mb-3 text-text-default relative">
          {content.heading}
          {Number(content.textBlockPosition) === 0 && (
            <IconArrowCurvy
              classes={
                'hidden top-1/2 -translate-y-1/2 lg:block absolute left-[calc(100%-30px)]'
              }
            />
          )}
        </h3>
      )}
      {content.body && (
        <p className="text-text-subdued mb-5 lg:mb-6">{content.body}</p>
      )}
      {content.link && content.linkText && (
        <a
          className="hidden no-underline px-2 lg:block button text-center lg:max-w-[150px] button--primary"
          href={content.link}
        >
          {content.linkText}
        </a>
      )}
    </div>
  </div>
);

const ContentBlock = ({ content, imageHeight, lazyload }) => {
  const Wrapper = content.link
    ? ({ children }) => (
        <a
          href={content.link}
          className="block relative"
          style={{ height: imageHeight, textDecoration: 'none' }}
        >
          {children}
        </a>
      )
    : ({ children }) => (
        <div className="relative" style={{ height: imageHeight }}>
          {children}
        </div>
      );

  return (
    <Wrapper>
      {content.image && (
        <Image
          classes="h-full object-cover"
          srcFormat={false}
          src={content.image}
          srcWidths={{ sm: 640 }}
          lazyload={lazyload}
        />
      )}
      <div
        style={{ color: content.textColor || '#fff' }}
        className="absolute px-2.5 lg:px-10 text-white top-0 left-0 w-full h-full flex items-center justify-center text-center"
      >
        <div>
          {content.heading && (
            <h3 className="h1 mb-1.5 lg:mb-2">{content.heading}</h3>
          )}
          {content.body && <p className="mb-4 body-2">{content.body}</p>}
          {content.link && content.linkText && (
            <span className="underline-offset-4 h7--medium">
              {content.linkText}
            </span>
          )}
        </div>
      </div>
    </Wrapper>
  );
};

export default ProductRecommendationsTabs;
